import { MathOperation, type StatusLevelModel } from '@/api/model'

/**
 * Pure, framework-agnostic level validation — ported 1:1 from the old portal
 * (`statusLevelValidation.ts`). The backend re-validates the same rules but only
 * throws a generic `StatusLevelRangeOverlap`; this client check turns that into
 * precise per-field messages. The error values are i18n keys in the NS
 * `service-modules-status`.
 */

export interface StatusLevelCandidate {
  name: string
  payoutPoints: number | null
  minPoints: number | null
  maxPoints: number | null
  maxPointsIsOpenEnded: boolean
  /** `null` = the level grants no status-based reward; operands are ignored then. */
  operation: MathOperation | null
  /** Raw input; empty = the incentivized score is used as this operand. */
  leftOperand: string
  rightOperand: string
}

export interface StatusLevelValidationErrors {
  name?: string
  payoutPoints?: string
  minPoints?: string
  maxPoints?: string
  range?: string
  leftOperand?: string
  rightOperand?: string
  operands?: string
}

const rangesOverlap = (
  aMin: number,
  aMax: number | null,
  bMin: number,
  bMax: number | null,
): boolean => {
  const aUpper = aMax ?? Number.POSITIVE_INFINITY
  const bUpper = bMax ?? Number.POSITIVE_INFINITY
  return aMin <= bUpper && bMin <= aUpper
}

export const parseIntegerInput = (value: string): number | null => {
  const trimmed = value.trim()
  if (!/^-?\d+$/.test(trimmed)) {
    return null
  }
  return parseInt(trimmed, 10)
}

/** Accepts a decimal comma as well, since users type German number formats. */
export const parseDecimalInput = (value: string): number | null => {
  const trimmed = value.trim().replace(',', '.')
  if (!/^-?\d+(\.\d+)?$/.test(trimmed)) {
    return null
  }
  return parseFloat(trimmed)
}

export const validateStatusLevel = (
  candidate: StatusLevelCandidate,
  siblings: StatusLevelModel[],
): StatusLevelValidationErrors => {
  const errors: StatusLevelValidationErrors = {}

  if (candidate.name.trim().length === 0) {
    errors.name = 'validation.nameRequired'
  } else if (candidate.name.trim().length > 100) {
    errors.name = 'validation.nameTooLong'
  }

  if (candidate.payoutPoints === null || candidate.payoutPoints < 0) {
    errors.payoutPoints = 'validation.payoutPointsInvalid'
  }

  if (candidate.minPoints === null) {
    errors.minPoints = 'validation.minPointsInvalid'
  }

  if (!candidate.maxPointsIsOpenEnded && candidate.maxPoints === null) {
    errors.maxPoints = 'validation.maxPointsInvalid'
  }

  if (candidate.minPoints !== null && candidate.maxPoints !== null && candidate.maxPoints < candidate.minPoints) {
    errors.maxPoints = 'validation.maxBelowMin'
  }

  if (candidate.maxPointsIsOpenEnded && siblings.some((level) => level.maxPoints === null)) {
    errors.maxPoints = 'validation.openLevelExists'
  }

  if (
    !errors.minPoints &&
    !errors.maxPoints &&
    candidate.minPoints !== null &&
    siblings.some((level) =>
      rangesOverlap(candidate.minPoints as number, candidate.maxPoints, level.minPoints, level.maxPoints),
    )
  ) {
    errors.range = 'validation.rangeOverlap'
  }

  if (candidate.operation !== null) {
    const leftEmpty = candidate.leftOperand.trim().length === 0
    const rightEmpty = candidate.rightOperand.trim().length === 0
    // Both empty would compute score <op> score — the backend requires one fixed operand.
    if (leftEmpty && rightEmpty) {
      errors.operands = 'validation.operandRequired'
    }
    if (!leftEmpty && parseDecimalInput(candidate.leftOperand) === null) {
      errors.leftOperand = 'validation.operandInvalid'
    }
    if (!rightEmpty) {
      const right = parseDecimalInput(candidate.rightOperand)
      if (right === null) {
        errors.rightOperand = 'validation.operandInvalid'
      } else if (candidate.operation === MathOperation.Divide && right === 0) {
        errors.rightOperand = 'validation.divideByZero'
      }
    }
  }

  return errors
}
